import React from 'react';
import { Button, Checkbox, FormControl, FormControlLabel, Snackbar, SnackbarOrigin, Tooltip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { IFinanceCalculatorContactRequest, IFinanceCalculatorState } from '../../../models/finance-calculator.model';
import { Col, Row } from 'reactstrap';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import ControllerInput from '../../Forms/ControllerInput/ControllerInput';
import FinanceCalculatorResultSummary from '../FinanceCalculatorResults/FinanceCalculatorResultSummary/FinanceCalculatorResultSummary';
import config from '../../../config';

interface SnackbarState extends SnackbarOrigin {
  message: string;
  open: boolean;
}

const ContactMeForm = ({ state, updateState }: {state: IFinanceCalculatorState, updateState: React.Dispatch<any>}) => {
  const [sbstate, setsbState] = React.useState<SnackbarState>({
    open: false,
    message: "Error",
    vertical: "bottom",
    horizontal: "center",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const { vertical, horizontal, open, message } = sbstate;

  const cssRow = "mb-3";
  const emailRegEx = new RegExp("[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$");
  const telNoEx = /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?#(\d{4}|\d{3}))?$/;

  const lblEmailSpecifics = "Email me a summary of this quote?";
  const lblOk2Contact = "I consent to you contacting me?";
  const lblNotes = "Additional information";
  const emailAddress = "Email address";
  const contactName = "Contact name";
  const contactNumber = "Contact number";
  const consent = "Consent";
  const isRequired = " is required";
  const isInvalid = " is invalid";
  const hasEmailFormatMessage = emailAddress + isInvalid;
  const hasTelNoFormatMessage = contactNumber + isInvalid;

  const hasGivenTheirConsent = (value: boolean) => { 
    // console.log("hasGivenTheirConsent: " + value);
    return value;
  };

  const registerOptions = {
    contactName: { required: contactName + isRequired },
    emailAddress: { 
      required: emailAddress,
      validate: {
        hasEmailFormat: (value: string) => emailRegEx.test(value) ? true : false,
      }
    },
    telNo: {
      required: contactNumber + isRequired,
      validate: {
        hasTelNoFormat: (value: string) => telNoEx.test(value) ? true : false,
      },
      minLength: {
        value: 10,
        message: contactNumber + " must have at least 10 characters"
      }
    },
    ok2contact: { 
      required: consent + isRequired,
      validate: {
        hasGivenConsent: hasGivenTheirConsent,
      },
    },
  };

  const basicDefault = {
    contactName: '',
    emailAddress: '',
    telNo: '',
    ok2contact: true,
    emailSpecifics: true,
    notes: ''
  };

  const { control, handleSubmit, formState: { errors, isValid }, register, setValue, watch } = useForm({
    defaultValues: basicDefault
  });
  const watchAllFields = watch(); // when pass nothing as argument, you are watching everything
  const handleError = (errors: any) => { };

  const getContactBody = (submitData: any): string => {
    const payload: IFinanceCalculatorContactRequest = {
      formdata: state.formdata,
      results: ( state.resultsdata? state.resultsdata[state.selectedIndex] : undefined),
      contactdata: submitData
    };
    return JSON.stringify(payload);
  }

  const handleClose = () => {
    setsbState({ ...sbstate, open: false });
  };

  const handleOk2ConsentChange = (e: any) => {
    setValue("ok2contact", e.target.checked);
  };

  const handleEmailSpecificsChange = (e: any) => {
    setValue("emailSpecifics", e.target.checked);
  };

  const onReset = () => {
    updateState({ ...state, contacting: false, contactsubmitted: false });
  };

  const onSubmit: SubmitHandler<any> = data => {
    postContactForm(data);
  };

  const postContactForm = (submitData: any) => {
    setSubmitting(true);
    fetch(config.REACT_APP_API_URL + "/api/financecalculator/contact", {
        method: "post", body: getContactBody(submitData),
        headers: {
            "Accept": "*/*",
            "Content-Type": "application/json",
        }
    })
        // .then((res) => res.json())
        .then((resp) => {
          if (!resp.ok) {
            throw new Error(resp.statusText);
          }
          updateState({ ...state, contacting: true, contactsubmitted: true, contactdata: submitData });
          setSubmitting(false);
        })
        .catch(err => { 
          // handleClick({ vertical: 'bottom', horizontal: 'center' }, err);
          alert(err);
          setSubmitting(false);
        })
  }

  const allErrors = () => {
    let res = "";
    if (errors?.contactName && errors?.contactName.message) {
      res = res + errors?.contactName.message + ", ";
    }
    if (errors?.emailAddress && errors?.emailAddress.message) {
      res = res + errors?.emailAddress.message + ", ";
    }
    if (errors?.emailAddress && errors.emailAddress.type === "hasEmailFormat") {
      res = res + hasEmailFormatMessage + ", ";
    }
    if (errors?.telNo && errors?.telNo.message) {
      res = res + errors?.telNo.message + ", ";
    }
    if (errors?.telNo && errors.telNo.type === "hasTelNoFormat") {
      res = res + hasTelNoFormatMessage + ", ";
    }
    if (errors?.ok2contact && errors.ok2contact.message) {
      res = res + errors.ok2contact.message + ", ";
    }

    return res;
  };
  
 return (
  <>
      <Row className={cssRow} key="row-summary">
        <Col sm={12}>
          <FinanceCalculatorResultSummary state={state}/>
        </Col>
      </Row>

      <form onSubmit={handleSubmit(onSubmit, handleError)} className="contactMeForm">
        <fieldset disabled={state.contactsubmitted} className={state.contactsubmitted ? "fieldset-submitted": ""}>

          <Row className={cssRow} key="row-name">
              <Col sm={12}>
                <FormControl sx={{ m: 0, width: { sm: 300 } }}>
                  <ControllerInput
                    key="contact-name" 
                    {...register("contactName", registerOptions.contactName)}
                    control={control}
                    label={"Contact Name"}
                  />
                </FormControl>
              </Col>
          </Row>

          <Row className={cssRow} key="row-phone">
              <Col sm={12}>
                <FormControl sx={{ m: 0, width: { sm: 180 } }}>
                  <ControllerInput
                    key="telephone-no" 
                    {...register("telNo", registerOptions.telNo)}
                    control={control}
                    label={"Contact number"}
                  />
                </FormControl>
              </Col>
          </Row>

          <Row className={cssRow} key="row-email">
              <Col sm={12}>
                <FormControl sx={{ m: 0, width: { sm: 300 } }}>
                  <ControllerInput
                    key="email-address" 
                    {...register("emailAddress", registerOptions.emailAddress)}
                    control={control}
                    label={"Email address"}
                  />
                </FormControl>
              </Col>
          </Row>

          <Row className={cssRow} key="row-notes">
              <Col sm={12}>
                <FormControl sx={{ m: 0, width: { sm: 300 } }}>
                  <ControllerInput
                    key="notes" 
                    {...register("notes")}
                    control={control}
                    placeholder="e.g. When is best to contact you?"
                    rows={3}
                    label={lblNotes}
                  />
                </FormControl>
              </Col>
          </Row>

          <Row className={cssRow} key="row-email-specifics">
            <Col>
              <FormControl sx={{ m: 0 }}>
                <Controller
                  key="known-email-specifics-controller"
                  {...register("emailSpecifics")}
                  control={control}
                  render={({ field }) =>
                    <FormControlLabel labelPlacement="end" label={lblEmailSpecifics} {...field} control=
                      {<Checkbox onChange={handleEmailSpecificsChange} checked={watchAllFields.emailSpecifics} />}
                    />
                  }
                />
              </FormControl>
            </Col>
          </Row>

          <Row className={cssRow} key="row-ok2contact">
            <Col>
              <FormControl sx={{ m: 0 }}>
                <Controller
                  key="known-ok2contact-controller"
                  {...register("ok2contact", registerOptions.ok2contact)}
                  control={control}
                  render={({ field }) =>
                    <FormControlLabel labelPlacement="end" label={lblOk2Contact} {...field} control=
                      {<Checkbox onChange={handleOk2ConsentChange} checked={watchAllFields.ok2contact}  />}
                    />
                  }
                />
              </FormControl>
            </Col>
          </Row>

          <Row sm={12} className={cssRow} key="row-submit">
            <Col sm={6}>
              <FormControl sx={{ m: 0 }}>
                {!state.contactsubmitted && (
                  <Button type="button" variant="contained" color="info" onClick={onReset}>Cancel&nbsp;
                  </Button>
                )}
              </FormControl>
            </Col>
            <Col sm={6}>
              <FormControl sx={{ m: 0 }}>
                {!state.contactsubmitted && (
                  <Button type="submit" disabled={submitting} variant="contained" color={isValid ? "primary" : "warning"}>
                    Submit
                    {submitting && (
                      <span>ting...</span>
                    )}
                    &nbsp;
                    
                    {!isValid && (
                      <Tooltip title={allErrors()} placement="right">
                        <WarningIcon />
                      </Tooltip>
                    )}
                  </Button>
                )}
              </FormControl>
            </Col>
          </Row>

          { !isValid && (
            <ul className="errorList">
              <li>
                <small className="text-danger">
                  {errors?.contactName && errors.contactName.message}
                </small>
              </li>
              <li>
                <small className="text-danger">
                  {errors?.emailAddress && errors.emailAddress.message}
                </small>
              </li>
              <li>
                <small className="text-danger">
                  {errors?.emailAddress && errors.emailAddress.type === "hasEmailFormat" && hasEmailFormatMessage}
                </small>
              </li>
              <li>
                <small className="text-danger">
                  {errors?.telNo && errors.telNo.message}
                </small>
              </li>
              <li>
                <small className="text-danger">
                  {errors?.telNo && errors.telNo.type === "hasTelNoFormat" && hasTelNoFormatMessage}
                </small>
              </li>
              <li>
                <small className="text-danger">
                  {errors?.ok2contact && errors.ok2contact.message}
                </small>
              </li>
            </ul>

          )}

          <Row className={cssRow + " debug"}>
            <Col>
              <FormControl sx={{ m: 0 }}>
                <div>
                  {JSON.stringify(errors)}
                </div>
              </FormControl>
            </Col>
          </Row>
          <Snackbar
            anchorOrigin={{ vertical, horizontal }}
            open={open}
            onClose={handleClose}
            message={ message }
            key={vertical + horizontal}
          />

        </fieldset>
      </form>
  </>
  ); 
};

export default ContactMeForm;
